import React from "react"
import autobind from "class-autobind"
import Table from "../common/Table"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody } from "material-ui"
import ExceptionColumn from "../../components/layout/RecordExceptionColumn"
import PunchImageToolTipColumn from "../../components/punch/PunchImageToolTipColumn"
import PunchMapToolTipColumn from "../../components/punch/PunchMapToolTipColumn"
import strings from "../../localization/strings"
import { browserHistory } from "react-router"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import WarningIcon from "material-ui/svg-icons/alert/warning"
import AuthenticationStore from "../../stores/AuthenticationStore"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import * as utils from "../../helpers/utils"
import * as setGlobalHierarchyLevel from "../../helpers/setGlobalHierarchyLevel"
import * as userHelper from "../../helpers/userHelper"
import * as permissionHelper from "../../helpers/permissionHelper"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import { IRecord, ERecordPageType, ERecordSearchFilter, EExceptionReviewDialogReviewType, EExceptionReviewDialogExceptionType } from "../../models"
import * as api from "../../api"
import AcceptSuspensePunchResultDialog from "./AcceptSuspensePunchResultDialog"
import { checkJobFieldAvailable } from "../../helpers/jobHelper"
import * as timeHelper from "../../helpers/timeHelper"
import moment from "moment"
import Thumbnail from "../common/Thumbnail"
import { checkPhaseFieldAvailable } from "../../helpers/punchHelper"

interface IProps {
	pageMode: ERecordPageType
	selectedFilter: ERecordSearchFilter
	records: IRecord[]
	reload(initOffset: boolean): void

	//pagination
	total?: number
	offset?: number
	limit?: number
	limitOptions?: number[]
	onPageClick?(offset: number): void
	onLimitChange?(limit: number): void

	//we need to hide Bio/Loc exception column when PunchSetException -> SuspensePunch
	hideExceptionCol?: boolean

	showEmployeeExternalId?: boolean

	showThumbnail?: boolean
}
interface IState {
	selectedRecords: IRecord[]
	acceptResult: any
}

export default class RecordTable extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		this.state = {
			selectedRecords: [],
			acceptResult: null
		}
		autobind(this)
	}

	render() {
		const showCheckBox = this.props.selectedFilter === ERecordSearchFilter.SuspensePunches &&
			permissionHelper.hasPermission(AuthenticationStore.Permissions.PunchRecords.EditSuspense)
		const selectedRecordCount = this.state.selectedRecords ? this.state.selectedRecords.length : 0

		const showLiveness = AccountStore.getLivenessDetectionEnabled()
		return (
			<div>
				{this.props.selectedFilter === ERecordSearchFilter.SuspensePunches && selectedRecordCount > 0 &&
					<div style={{ background: "#cccccc", padding: 10, display: "flex", justifyContent: "space-between" }}>
						<span style={{ fontSize: 18, fontWeight: 500, marginTop: 4 }}>
							{strings.formatString(strings.x_Records_Selected, selectedRecordCount)}
						</span>
						<div>
							<i
								className="fa fa-check itemActionButton"
								onClick={() => AppStateStore.setConfirmDialogContent({
									content: strings.Are_you_sure_you_want_to_accept_the_selected_suspense_punches,
									confirm: () => {
										AppStateStore.setProgressSnackbarContent("loading")
										api.acceptSuspensePunches(this.state.selectedRecords.map(i => i.Punch.Id)).then(res => {
											AppStateStore.setProgressSnackbarContent(null)
											this.setState({ acceptResult: res.data })
										})
									}
								})}
								style={{ fontSize: 25, marginRight: 15 }}
							/>
							<i
								className="fa fa-remove itemActionButton"
								onClick={() => AppStateStore.setConfirmDialogContent({
									content: strings.Are_you_sure_you_want_to_delete_reject_the_selected_suspense_punches,
									confirm: () => {
										AppStateStore.setProgressSnackbarContent("loading")
										api.deleteRejectSuspensePunches(this.state.selectedRecords.map(i => i.Punch.Id)).then(() => {
											AppStateStore.setProgressSnackbarContent(strings.Successfully_deleted)
											this.props.reload(true)
										})
									}
								})}
								style={{ fontSize: 25 }}
							/>
						</div>
					</div>
				}
				<Table
					items={this.props.records}
					selectedItems={this.state.selectedRecords}
					total={this.props.total}
					offset={this.props.offset}
					limit={this.props.limit}
					limitOptions={this.props.limitOptions}
					handleSelect={selectedRecords => this.setState({ selectedRecords })}
					onPageClick={this.props.onPageClick}
					onLimitChange={this.props.onLimitChange}
					selectObjectName="Punch"
				>
					<TableHeader adjustForCheckbox={showCheckBox} displaySelectAll={showCheckBox}>
						<TableRow
							className={"ShortSelectBoxTableHeader"}
						>
							<TableHeaderColumn style={styles.suspenseCol} />
							<TableHeaderColumn className="TextOverflow" title={strings.Image} style={styles.imageCol}>{strings.Image}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Map} style={styles.mapCol}>{strings.Map}</TableHeaderColumn>
							{this.props.pageMode !== ERecordPageType.UserDetail &&
								<TableHeaderColumn className="TextOverflow" title={strings.Employee} style={styles.columnValue}>{strings.Employee}</TableHeaderColumn>
							}
							<TableHeaderColumn className="TextOverflow" title={strings.Hierarchy} style={styles.columnValue}>{strings.Hierarchy}</TableHeaderColumn>
							{AccountStore.getJobsEnabled() &&
								<TableHeaderColumn className="TextOverflow" title={checkJobFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job} style={styles.columnValue}>{checkJobFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job}</TableHeaderColumn>
							}
							{AccountStore.getPhasesEnabled() &&
								<TableHeaderColumn className="TextOverflow" title={strings.Phase} style={styles.columnValue}>{ checkPhaseFieldAvailable("singular") ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel : strings.Phase}</TableHeaderColumn>
							}
							<TableHeaderColumn className="TextOverflow" title={strings.Device_Name} style={styles.columnValue}>{strings.Device_Name}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Biometric_Score} style={styles.columnValue}>{strings.Biometric_Score}</TableHeaderColumn>
							{showLiveness &&
								<TableHeaderColumn className="TextOverflow" title={strings.Liveness_Score} style={styles.columnValue}>{strings.Liveness_Score}</TableHeaderColumn>
							}
							{!this.props.hideExceptionCol &&
								<TableHeaderColumn className="TextOverflow" title={strings.Exception} style={styles.columnValue}>{strings.Exception}</TableHeaderColumn>
							}
							<TableHeaderColumn className="TextOverflow" title={strings.Date} style={styles.columnValue}>{strings.Date}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Time} style={styles.columnValue}>{strings.Time}</TableHeaderColumn>
						</TableRow>
					</TableHeader>
					<TableBody showRowHover={true} deselectOnClickaway={false} displayRowCheckbox={showCheckBox}>
						{this.props.records.map((row, index) => {
							const jobName = row.Punch.JobName ? row.Punch.JobName : ""
							const phaseName = row.Punch.PhaseName ? row.Punch.PhaseName : ""
							return (
								<TableRow
									key={index}
									className={"ShortSelectBoxTableRow"}
									style={row.Punch.PunchExceptions && row.Punch.PunchExceptions.some(function (element, index, array) { return !element.Reviewed }) ? styles.unRead : styles.read}
									selected={this.state.selectedRecords.find(e => e.Punch.Id === row.Punch.Id) ? true : false}
								>
									<TableRowColumn style={styles.suspenseCol}>
										{row.Punch.Suspense &&
											<WarningIcon
												style={{ color: "#FFC400", width: 20, height: 20 }}
												onClick={e => {
													e.stopPropagation()
													AppStateStore.setSuspensePunchReviewDialogContent({
														punchId: row.Punch.Id,
														records: this.props.records.filter(i => i.Punch.Suspense),
														changedCallback: () => this.props.reload(true)
													})
												}}
											/>
										}
									</TableRowColumn>
									<TableRowColumn style={styles.imageCol}>
										{this.props.showThumbnail && row.Punch.ImageS3Url ?
											<Thumbnail
												thumbnailId={row.Punch.Id}
												imageS3Url={row.Punch.ImageS3Url}
												thumbnailSize={35}
											/> :
											<PunchImageToolTipColumn punch={row.Punch} />
										}
									</TableRowColumn>
									<TableRowColumn style={styles.mapCol}>
										<PunchMapToolTipColumn punch={row.Punch} />
									</TableRowColumn>
									{this.props.pageMode !== ERecordPageType.UserDetail &&
										<TableRowColumn style={styles.columnValue}>
											<div
												onClick={e => {
													e.stopPropagation()
													permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.ViewEmployees) && (
														row.UserDeleted ?
															AppStateStore.setProgressSnackbarContent(strings.This_employee_has_been_deleted) :
															browserHistory.push(`/user-details/${row.UserId}/${AccountStore.getTimecardEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards) ? "timecards" : "positions"}`)
													)
												}}
											>
												{row.UserFirstName} {row.UserLastName}
												<br />
												{this.props.showEmployeeExternalId && row.UserExternalId &&
													<span style={{ color: "rgb(158, 158, 158)", fontSize: 10 }}>Emp #: {row.UserExternalId}</span>
												}
											</div>
										</TableRowColumn>
									}
									<TableRowColumn style={styles.columnValue}>
										<div
											onClick={e => {
												e.stopPropagation()
												const hierarchyItemId = row.Punch.HierarchyItemId
												if (hierarchyItemId && hierarchyItemId !== HierarchySettingsStore.getGlobalLevelItemFilterId()) {
													const hierarchyLevel = hierarchyHelper.getHierarchyItemById(hierarchyItemId)
													if (!hierarchyLevel) {
														AppStateStore.setProgressSnackbarContent(strings.Hierarchy_is_Deleted)
													}
													else {
														const accessRight = userHelper.getSupervisorAccessPermissionForHierarchy(hierarchyLevel)
														if (accessRight && hierarchyLevel.Active) {
															browserHistory.push("/hierarchy/location")
															setGlobalHierarchyLevel.setGlobalHierarchyLevel(hierarchyLevel)
														}
														else if (!accessRight) {
															AppStateStore.setProgressSnackbarContent(strings.You_cant_access_this_hierarchy_item)
														}
														else if (accessRight && !hierarchyLevel.Active) {
															AppStateStore.setProgressSnackbarContent(strings.Hierarchy_is_Deleted)
														}
													}
												} else if (hierarchyItemId && hierarchyItemId === HierarchySettingsStore.getGlobalLevelItemFilterId()) {
													browserHistory.push("/hierarchy/location")
												}
											}}
										>
											{row.LocationName}
										</div>
									</TableRowColumn>
									{AccountStore.getJobsEnabled() &&
										<TableRowColumn style={styles.columnValue} title={jobName}>{jobName}</TableRowColumn>
									}
									{AccountStore.getPhasesEnabled() &&
										<TableRowColumn style={styles.columnValue} title={phaseName}>{phaseName}</TableRowColumn>
									}
									<TableRowColumn style={styles.columnValue} title={row.DeviceName}>{row.DeviceName}</TableRowColumn>
									<TableRowColumn style={styles.columnValue}>{utils.getBiometricScore(row.Punch)}</TableRowColumn>
									{showLiveness &&
										<TableRowColumn style={styles.columnValue}>{utils.getLivenessScore(row.Punch)}</TableRowColumn>
									}
									{!this.props.hideExceptionCol &&
										<TableRowColumn style={styles.columnValue}>
											<ExceptionColumn
												selectedFilter={this.props.selectedFilter}
												punch={row.Punch}
												onClickException={(exceptionPunchId, clickedExceptionType) =>
													AppStateStore.setPunchExceptionReviewDialogContent({
														exceptionPunchId,
														clickedExceptionType,
														punches: this.props.records.map(record => record.Punch),
														availableExceptionTypes: this.props.selectedFilter === ERecordSearchFilter.BioExceptions ?
															[EExceptionReviewDialogExceptionType.Biometric] :
															this.props.selectedFilter === ERecordSearchFilter.LocExceptions ?
																[EExceptionReviewDialogExceptionType.Location] :
																[EExceptionReviewDialogExceptionType.Biometric, EExceptionReviewDialogExceptionType.Location]
														,
														reviewedType: EExceptionReviewDialogReviewType.All,
														closeCallback: () => this.props.reload(false)
													})}
											/>
										</TableRowColumn>
									}
									<TableRowColumn style={styles.columnValue}>{moment(timeHelper.keepConvertUTCToLocal(row.Punch.DateTimeLocal)).format("MMM. DD, YYYY")}</TableRowColumn>
									<TableRowColumn style={styles.columnValue}>{moment(timeHelper.keepConvertUTCToLocal(row.Punch.DateTimeLocal)).format("h:mm A")}</TableRowColumn>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
				{this.state.acceptResult &&
					<AcceptSuspensePunchResultDialog
						selectedRecords={this.state.selectedRecords}
						acceptResult={this.state.acceptResult}
						handleClose={() => {
							const notAcceptedPunchAudits = this.state.selectedRecords.map(i => this.state.acceptResult[i.Punch.Id]).filter(i => i !== null)
							this.setState({ acceptResult: null })
							this.state.selectedRecords.length !== notAcceptedPunchAudits.length &&
								this.props.reload(true)
						}}
					/>
				}
			</div>
		)
	}
}

const styles = {
	read: {
		fontWeight: "normal",
		cursor: "pointer"
	},
	unRead: {
		fontWeight: "bold",
		cursor: "pointer"
	},
	columnValue: {
		textAlign: "center",
		whitespace: "normal",
		paddingLeft: 10,
		paddingRight: 10
	},
	imageCol: {
		width: 45,
		paddingLeft: 2,
		paddingRight: 2,
		textAlign: "center",
	},
	mapCol: {
		width: 45,
		paddingLeft: 2,
		paddingRight: 2,
		textAlign: "center",
	},
	suspenseCol: {
		width: 20,
		paddingLeft: 10,
		paddingRight: 10
	}
}